import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

type Status = "loading" | "success" | "error";

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Derive initial state from token presence (avoids setState in effect)
  const [status, setStatus] = useState<Status>(() =>
    token ? "loading" : "error",
  );
  const [contactName, setContactName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() =>
    token ? null : "Invalid unsubscribe link - missing token",
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    const unsubscribe = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/unsubscribe?token=${encodeURIComponent(token)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          },
        );

        const data = await response.json();

        if (!response.ok) {
          setStatus("error");
          setError(data.error || "Failed to unsubscribe");
          return;
        }

        setStatus("success");
        // Extract contact name from message if available
        if (data.message) {
          const match = data.message.match(/^(.+?) has been unsubscribed/);
          if (match) {
            setContactName(match[1]);
          }
        }
      } catch {
        setStatus("error");
        setError("Failed to process unsubscribe request");
      }
    };

    unsubscribe();
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.50",
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          p: { xs: 3, md: 4 },
          textAlign: "center",
        }}
      >
        {status === "loading" && (
          <>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" fontWeight={600}>
              Processing...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unsubscribing from market reports
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: "success.main", mb: 2 }}
            />
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              Unsubscribed
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {contactName ? (
                <>
                  <strong>{contactName}</strong> has been unsubscribed from
                  market reports.
                </>
              ) : (
                "You have been unsubscribed from market reports."
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              You will no longer receive property market updates.
            </Typography>
          </>
        )}

        {status === "error" && (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
            <Typography
              variant="h5"
              fontWeight={700}
              color="error"
              sx={{ mb: 1 }}
            >
              Unable to Unsubscribe
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please contact support if this problem persists.
            </Typography>
          </>
        )}
      </Card>
    </Box>
  );
}
